import React, { useState, useCallback } from "react";
import { CardBody, Button, FormGroup, Label, Input, Alert, Spinner } from "reactstrap";
import Select2 from "react-select2-wrapper";
import "select2/dist/css/select2.min.css";
import userServices from "services/userServices";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";

const REASON_CONFIGS = [
  { id: "Suspicious Activity", labelKey: "page.blockedIps.reasons.suspiciousActivity", defaultText: "Suspicious Activity" },
  { id: "Abuse / Malicious Traffic", labelKey: "page.blockedIps.reasons.maliciousTraffic", defaultText: "Abuse / Malicious Traffic" },
  { id: "Brute Force Attempt", labelKey: "page.blockedIps.reasons.bruteForce", defaultText: "Brute Force Attempt" },
  { id: "Bot / Automated Traffic", labelKey: "page.blockedIps.reasons.botTraffic", defaultText: "Bot / Automated Traffic" },
  { id: "Security Investigation", labelKey: "page.blockedIps.reasons.securityInvestigation", defaultText: "Security Investigation" },
  { id: "Other", labelKey: "page.blockedIps.reasons.other", defaultText: "Other" },
];

function isValidIpAddress(ip) {
  if (!ip || typeof ip !== "string") return false;
  const trimmed = ip.trim();
  const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
  return ipv4Regex.test(trimmed) || ipv6Regex.test(trimmed) || trimmed === "::1";
}

const BlockedIPs = () => {
  const { t, language } = useTranslation();
  const { notify } = useNotification();

  const [ipAddress, setIpAddress] = useState("");
  const [blockType, setBlockType] = useState("TEMPORARY");
  const [durationMs, setDurationMs] = useState(String(15 * 60 * 1000));
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reasonOptions = [
    { id: "", text: t("page.blockedIps.selectReason", "Select reason") },
    ...REASON_CONFIGS.map((r) => ({ id: r.id, text: t(r.labelKey, r.defaultText) })),
  ];

  const durationOptions = [
    { id: String(15 * 60 * 1000),      text: t("page.blockedIps.durations.15mLong", "15 Minutes") },
    { id: String(2 * 60 * 60 * 1000),  text: t("page.blockedIps.durations.2hLong", "2 Hours") },
    { id: String(24 * 60 * 60 * 1000), text: t("page.blockedIps.durations.24hLong", "24 Hours") },
    { id: String(7 * 24 * 60 * 60 * 1000), text: t("page.blockedIps.durations.7dLong", "7 Days") },
  ];

  const handleBlockIP = useCallback(async () => {
    const trimmedIp = ipAddress.trim();
    if (!trimmedIp) {
      const msg = t("page.blockedIps.validation.ipRequired", "IP Address is required.");
      setError(msg);
      return notify("error", msg);
    }

    if (!isValidIpAddress(trimmedIp)) {
      const msg = t("page.blockedIps.validation.invalidIp", "Please enter a valid IP address.");
      setError(msg);
      return notify("error", msg);
    }

    if (!reason) {
      const msg = t("page.blockedIps.validation.reasonRequired", "Please select a block reason.");
      setError(msg);
      return notify("error", msg);
    }

    if (reason === "Other" && !customReason.trim()) {
      const msg = t("page.blockedIps.validation.customReasonRequired", "Please provide a reason when 'Other' is selected.");
      setError(msg);
      return notify("error", msg);
    }

    const blockedUntil = blockType === "TEMPORARY"
      ? new Date(Date.now() + Number(durationMs)).toISOString()
      : null;

    setLoading(true);
    try {
      const res = await userServices.updateIPStatus({
        ipAddress: trimmedIp,
        blockType,
        reason,
        customReason: reason === "Other" ? customReason.trim() : undefined,
        blockedUntil,
        isActive: true,
      });

      if (res?.success) {
        notify("success", t("page.blockedIps.blockSuccess", "IP blocked successfully"));
        setIpAddress("");
        setBlockType("TEMPORARY");
        setDurationMs(String(15 * 60 * 1000));
        setReason("");
        setCustomReason("");
        setError("");
      } else {
        notify("error", t(res?.error || "common.failed", res?.message || res?.error || "Failed to block IP"));
      }
    } catch (err) {
      console.error("Block IP failed:", err);
      notify("error", t("page.users.toast.somethingWentWrong", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  }, [ipAddress, blockType, durationMs, reason, customReason, t, notify]);

  const isFormInvalid =
    !ipAddress.trim() ||
    !reason ||
    (reason === "Other" && !customReason.trim()) ||
    loading;

  return (
    <CardBody>
      {error && <Alert color="danger" className="py-2 text-sm">{error}</Alert>}

      <FormGroup className="mb-3">
        <Label className="text-sm font-weight-600 mb-1" htmlFor="settingsIpInput">
          {t("table.headers.ipAddress", "IP Address")} <span className="text-danger">*</span>
        </Label>
        <Input
          type="text"
          id="settingsIpInput"
          className="form-control"
          placeholder="e.g. 192.168.1.1 or ::1"
          value={ipAddress}
          onChange={(e) => {
            setIpAddress(e.target.value);
            if (error) setError("");
          }}
          disabled={loading}
        />
      </FormGroup>

      <FormGroup className="mb-3">
        <Label className="text-sm font-weight-600 mb-1 d-block">
          {t("table.headers.blockType", "Block Type")}
        </Label>
        <div className="d-flex align-items-center mt-1">
          <div className="custom-control custom-radio custom-control-inline mr-4">
            <input
              type="radio"
              id="settingsBlockTypeTemp"
              name="settingsBlockTypeRadio"
              className="custom-control-input"
              checked={blockType === "TEMPORARY"}
              onChange={() => setBlockType("TEMPORARY")}
              disabled={loading}
            />
            <label className="custom-control-label font-weight-500 cursor-pointer" htmlFor="settingsBlockTypeTemp">
              {t("page.blockedIps.types.temporary", "Temporary")}
            </label>
          </div>
          <div className="custom-control custom-radio custom-control-inline">
            <input
              type="radio"
              id="settingsBlockTypePerm"
              name="settingsBlockTypeRadio"
              className="custom-control-input"
              checked={blockType === "PERMANENT"}
              onChange={() => setBlockType("PERMANENT")}
              disabled={loading}
            />
            <label className="custom-control-label font-weight-500 cursor-pointer" htmlFor="settingsBlockTypePerm">
              {t("page.blockedIps.types.permanent", "Permanent")}
            </label>
          </div>
        </div>
      </FormGroup>

      {blockType === "TEMPORARY" && (
        <FormGroup className="mb-3">
          <Label className="text-sm font-weight-600 mb-1">
            {t("table.headers.duration", "Duration")}
          </Label>
          <Select2
            key={`setting-duration-${language}`}
            className="form-control"
            value={durationMs}
            onChange={(e) => setDurationMs(e.target.value)}
            disabled={loading}
            options={{
              width: "100%",
              data: durationOptions,
            }}
          />
        </FormGroup>
      )}

      {blockType === "PERMANENT" && (
        <Alert color="warning" className="text-sm mb-3">
          <strong>{t("common.warning", "Warning")}:</strong>{" "}
          {t(
            "page.blockedIps.permanentWarning",
            "Permanent blocks will not auto-expire and require explicit admin action to unblock."
          )}
        </Alert>
      )}

      <FormGroup className="mb-3">
        <Label className="text-sm font-weight-600 mb-1">
          {t("table.headers.reason", "Reason")} <span className="text-danger">*</span>
        </Label>
        <Select2
          key={`setting-reason-${language}`}
          className="form-control"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError("");
          }}
          disabled={loading}
          options={{
            width: "100%",
            data: reasonOptions,
          }}
        />
      </FormGroup>

      {reason === "Other" && (
        <FormGroup className="mb-3">
          <Label className="text-sm font-weight-600 mb-1" htmlFor="settingsCustomReasonInput">
            {t("page.blockedIps.customReason", "Specify Reason")} <span className="text-danger">*</span>
          </Label>
          <Input
            type="textarea"
            id="settingsCustomReasonInput"
            className="form-control"
            rows={3}
            placeholder={t("page.blockedIps.customReasonPlaceholder", "Enter detailed reason...")}
            value={customReason}
            onChange={(e) => {
              setCustomReason(e.target.value);
              if (error) setError("");
            }}
            disabled={loading}
          />
        </FormGroup>
      )}

      <Button
        color="danger"
        className="mt-2 font-weight-500 shadow-none border-0 btn-ip-block-submit"
        onClick={handleBlockIP}
        disabled={isFormInvalid}
      >
        {loading && <Spinner size="sm" className="mr-2" />}
        {t("page.blockedIps.blockButton", "Block IP")}
      </Button>
    </CardBody>
  );
};

export default BlockedIPs;
